import React from 'react';
import { Button, ButtonVariant } from '@/shared/components/Buttons';
import {
  Card,
  CardBody,
  CardContent,
  CardStyle,
} from '@/shared/components/Card';
import { Dialog } from '@/shared/components/Dialog';
import { Tooltip } from '@shared/components/Tooltip';
import ActivateIcon from '@/assets/svg/activate.svg';
import DeactivateIcon from '@/assets/svg/deactivate.svg';

// ============================================================================
// Types
// ============================================================================

export interface UpdateSubscriptionStatusDialogProps {
  open: boolean;
  onClose: () => void;
  planName: string;
  isActive: boolean; // Current status - if true, will deactivate; if false, will activate
  onConfirm: () => void;
  isSubmitting?: boolean;
}

// ============================================================================
// Icon Component (Switch/Toggle Icon)
// ============================================================================

const SwitchIcon: React.FC<{ isActivating: boolean }> = ({ isActivating }) => {
  const Icon = isActivating ? ActivateIcon : DeactivateIcon;
  return <Icon />;
};

// ============================================================================
// Component
// ============================================================================

export const UpdateSubscriptionStatusDialog: React.FC<
  UpdateSubscriptionStatusDialogProps
> = ({
  open,
  onClose,
  planName,
  isActive,
  onConfirm,
  isSubmitting = false,
}) => {
  // Truncate plan name to ~40 characters as per Figma note
  const shouldTruncate = planName.length > 40;

  const isActivating = !isActive;
  const title = isActivating
    ? 'Aktywować plan subskrypcji?'
    : 'Dezaktywować plan subskrypcji?';
  const confirmButtonLabel = isActivating ? 'Aktywuj' : 'Dezaktywuj';
  const confirmButtonLabelLoading = isActivating
    ? 'Aktywowanie…'
    : 'Dezaktywowanie…';

  // Format description with tooltip for truncated name
  const formatDescription = () => {
    const prefix = isActivating
      ? 'Spowoduje to natychmiastowe udostępnienie planu subskrypcji '
      : 'Spowoduje to natychmiastowe zablokowanie planu subskrypcji ';
    const suffix = isActivating
      ? 'Czy na pewno chcesz aktywować ten plan?'
      : 'Czy na pewno chcesz dezaktywować ten plan?';

    return (
      <>
        <span>{prefix}</span>
        {shouldTruncate ? (
          <Tooltip content={planName} align="right">
            <span
              className="inline-block overflow-hidden font-medium text-ellipsis whitespace-nowrap"
              style={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 500,
                maxWidth: '200px',
                display: 'inline-block',
                verticalAlign: 'baseline',
              }}
            >
              {planName}.
            </span>
          </Tooltip>
        ) : (
          <span
            className="font-medium"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 500,
            }}
          >
            {planName}.
          </span>
        )}{' '}
        <span>{suffix}</span>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      maxWidth={432}
    >
      <Card style={CardStyle.Elevated} width={432}>
        <CardBody padding="large">
          <div className="flex flex-col items-center gap-6 text-center">
            {/* Icon Section - 137px icon matching AddUserModal structure */}
            <div className="flex items-center justify-center w-[137px] h-[137px]">
              <SwitchIcon isActivating={isActivating} />
            </div>

            {/* Content Section - Using CardContent like AddUserModal */}
            <CardContent
              title={title}
              className="!px-0 !py-0 flex flex-col items-center !gap-4 !text-[#474A4C] [&>p]:!font-normal"
            >
              <p
                className="mt-2 text-center"
                style={{
                  fontSize: 14,
                  lineHeight: '20px',
                }}
              >
                {formatDescription()}
              </p>
            </CardContent>

            {/* CTA Buttons - matching AddUserModal structure exactly */}
            <div className="flex flex-row items-center justify-center w-full gap-3 px-5">
              <Button
                label="Anuluj"
                onClick={onClose}
                disabled={isSubmitting}
                className={`!bg-transparent !h-[40px] !rounded-[8px] border !text-labelLarge !text-on-surface-variant hover:!bg-neutral-95 hover:border-neutral-70 w-full ${
                  isSubmitting
                    ? '!bg-[#181A1B1A] !border-[#DCDDE0] opacity-38 !text-[#181A1B] !cursor-not-allowed'
                    : '!border-[#DCDDE0] cursor-pointer'
                }`}
              />
              <Button
                label={
                  isSubmitting ? confirmButtonLabelLoading : confirmButtonLabel
                }
                variant={
                  isActivating ? ButtonVariant.Success : ButtonVariant.Error
                }
                onClick={onConfirm}
                disabled={isSubmitting}
                className="!h-[40px] !rounded-[8px] !text-labelLarge !text-white w-full cursor-pointer"
              />
            </div>
          </div>
        </CardBody>
      </Card>
    </Dialog>
  );
};

export default UpdateSubscriptionStatusDialog;
